/**
 * Statistics (copy of onlyfans.com/my/statistics, captured 2026-10-07, see
 * docs/of_statistics_capture_2026-10-07/SPEC.md): the tab layout, the exact
 * OnlyFans queries every view sends through the CRM's allow-listed
 * POST /v1/statistics/of, and the pure parsing/formatting the views share.
 * Ported 1:1 from crm/src/public/statistics/statistics.js — nothing here is
 * guessed or derived beyond what that page does.
 */

export type Raw = Record<string, unknown>
export type Query = Record<string, string | number>
export interface Range {
  startDate: string
  endDate: string
}

export const PAGE_SIZE = 10
export const DAY_MS = 86_400_000
export const OF_STATISTICS = 'https://onlyfans.com/my/statistics'
/** OnlyFans' public web app token (part of OF's own invoice link). */
export const OF_APP_TOKEN = '33d57ade8c02dbc5a333db99ff9ae26a'

// ── Tabs ─────────────────────────────────────────────────────────────────
// Labels are keys of the `l` group in the ofStatistics namespace.

export type TabId = 'statements' | 'overview' | 'engagement' | 'reach' | 'fans'
export interface SubDef {
  id: string
  label: string
  path: string
}
export interface TabDef {
  id: TabId
  subs: SubDef[]
}

const sub = (id: string, label: string, path: string): SubDef => ({ id, label, path })

export const TABS: TabDef[] = [
  {
    id: 'statements',
    subs: [
      sub('earnings', 'earnings', 'statements/earnings'),
      sub('payouts', 'payoutRequests', 'statements/payout-request'),
      sub('chargebacks', 'chargebacks', 'statements/disputes'),
      sub('referrals', 'referrals', 'statements/referrals/earnings'),
    ],
  },
  {
    id: 'overview',
    subs: [
      sub('earnings', 'earnings', 'overview/earnings'),
      sub('highlights', 'topHighlights', 'overview/top-highlights'),
      sub('streak', 'activityStreak', 'overview/streaks?tab=current'),
    ],
  },
  {
    id: 'engagement',
    subs: [
      sub('posts', 'posts', 'engagement/posts'),
      sub('messages', 'messages', 'engagement/messages'),
      sub('streaming', 'streaming', 'engagement/streaming'),
      sub('stories', 'stories', 'engagement/stories'),
    ],
  },
  {
    id: 'reach',
    subs: [
      sub('visitors', 'profileVisitors', 'reach/profile-visitors'),
      sub('promotions', 'promotions', 'reach/promotions'),
      sub('trials', 'trialLinks', 'reach/trial-links'),
      sub('tracking', 'trackingLinks', 'reach/tracking-links'),
    ],
  },
  {
    id: 'fans',
    subs: [
      sub('subscriptions', 'subscriptions', 'fans/subscriptions'),
      sub('top', 'topFans', 'fans/top-fans'),
      sub('activity', 'fansActivity', 'fans/activity'),
    ],
  },
]

/** The tab and sub-tab a `view=<tab>/<sub>` query asks for (e.g. `reach/promotions`), or null. */
export function viewFromQuery(query: string | undefined): { tab: TabId; sub: string } | null {
  const [tab, sub] = (new URLSearchParams(query ?? '').get('view') ?? '').split('/')
  const def = TABS.find((d) => d.id === tab)
  if (!def) return null
  return { tab: def.id, sub: def.subs.some((x) => x.id === sub) ? sub! : DEFAULT_SUBS[def.id] }
}

export const DEFAULT_SUBS: Record<TabId, string> = {
  statements: 'earnings',
  overview: 'earnings',
  engagement: 'posts',
  reach: 'visitors',
  fans: 'subscriptions',
}

/** earnings/chart filters (filter[{f}_count] / filter[{f}_amount]), the key each comes back under, `tx` = payouts/transactions `type`. */
export interface EarningType {
  id: string
  label: string
  side: string
  filter: string
  key: string
  tx: string
  tone: Tone
}
export type Tone = 'total' | 's1' | 's2' | 's3' | 's4' | 's5' | 's6'

export const EARNING_TYPES: EarningType[] = [
  { id: 'total', label: 'all', side: 'total', filter: 'total', key: 'total', tx: '', tone: 'total' },
  { id: 'subscribes', label: 'subscriptions', side: 'subscriptions', filter: 'subscribes', key: 'subscribes', tx: 'subscribes', tone: 's1' },
  { id: 'tips', label: 'tips', side: 'tips', filter: 'tips', key: 'tips', tx: 'tips', tone: 's2' },
  { id: 'post', label: 'posts', side: 'posts', filter: 'post', key: 'post', tx: 'post', tone: 's3' },
  { id: 'messages', label: 'messages', side: 'messages', filter: 'messages', key: 'chat_messages', tx: 'chat_messages', tone: 's4' },
  { id: 'stream', label: 'streams', side: 'streams', filter: 'stream', key: 'stream', tx: 'stream', tone: 's6' },
]
export const CALL_TYPE: EarningType = { id: 'call', label: 'calls', side: 'calls', filter: 'call', key: 'call', tx: 'call', tone: 's5' }

/** Statements' right column "Earnings": OF's order (Total, Subscriptions, Tips, Messages, Posts, Streams) then Calls. */
export const SIDE_EARNING_TYPES: EarningType[] = [
  ...['total', 'subscribes', 'tips', 'messages', 'post', 'stream'].map((id) => EARNING_TYPES.find((t) => t.id === id)!),
  CALL_TYPE,
]

/** Overview → Earnings breakdown (earnings/chart with *_amount filters). */
export const OVERVIEW_ROWS = [
  { key: 'total', filter: 'total_amount', label: 'total' },
  { key: 'subscribes', filter: 'subscribes_amount', label: 'subscriptions' },
  { key: 'tips', filter: 'tips_amount', label: 'tips' },
  { key: 'tips_stream', filter: 'tips_stream_amount', label: 'streamTips' },
  { key: 'post', filter: 'post_amount', label: 'posts' },
  { key: 'chat_messages', filter: 'messages_amount', label: 'messages' },
  { key: 'ref', filter: 'ref_amount', label: 'referrals' },
  { key: 'stream', filter: 'stream_amount', label: 'streams' },
] as const

export type PillDef = readonly [id: string, label: string]

export const PILLS = {
  referrals: [['earnings', 'referralEarnings'], ['creators', 'referredCreators'], ['users', 'referredUsers']],
  posts: [['purchases', 'purchases'], ['tips', 'tips'], ['views', 'views'], ['likes', 'likes'], ['comments', 'comments']],
  messages: [['group', 'massMessages'], ['direct', 'directMessages']],
  streaming: [['purchases', 'purchases'], ['tips', 'tips'], ['views', 'viewers'], ['likes', 'likes'], ['comments', 'comments']],
  stories: [['tips', 'tips'], ['views', 'viewers'], ['likes', 'likes'], ['comments', 'comments']],
  visitors: [['total', 'all'], ['guests', 'guests'], ['users', 'users']],
  subscriptions: [['total', 'all'], ['renew', 'renews'], ['new', 'newSubscribers']],
  topFans: [['total', 'total'], ['subscribes', 'subscriptions'], ['tips', 'tips'], ['messages', 'messages'], ['post', 'posts'], ['streams', 'streams']],
  topPost: [['purchases', 'purchases'], ['tips', 'tips'], ['views', 'views'], ['likes', 'likes'], ['comments', 'comments']],
  topStory: [['tips', 'tips'], ['views', 'viewers'], ['likes', 'likes'], ['comments', 'comments']],
  topFan: [['total', 'total'], ['subscriptions', 'subscriptions'], ['tips', 'tips'], ['messages', 'messages'], ['streams', 'streams']],
} satisfies Record<string, PillDef[]>

export const pillLabel = (pills: readonly PillDef[], id: string, fallback = 'total') => pills.find(([p]) => p === id)?.[1] ?? fallback

/** subscribedOnData field for each Top fans `by`. */
export const TOP_FAN_SUMS: Record<string, string> = {
  total: 'totalSumm',
  subscribes: 'subscribesSumm',
  tips: 'tipsSumm',
  messages: 'messagesSumm',
  post: 'postsSumm',
  streams: 'streamsSumm',
}

/** The per-page choices (pills and in-card tabs), as the CRM page keeps them. */
export interface Pills {
  earnings: string
  referrals: string
  overviewRow: string
  posts: string
  messages: string
  streaming: string
  stories: string
  visitors: string
  subscriptions: string
  topFans: string
  topPost: string
  topStory: string
  topFan: string
  topFanTab: string
  streakTab: string
}

export const DEFAULT_PILLS: Pills = {
  earnings: 'total',
  referrals: 'earnings',
  overviewRow: 'total',
  posts: 'purchases',
  messages: 'group',
  streaming: 'purchases',
  stories: 'tips',
  visitors: 'total',
  subscriptions: 'total',
  topFans: 'total',
  topPost: '',
  topStory: '',
  topFan: 'total',
  topFanTab: 'subscription',
  streakTab: 'current',
}

/** The OnlyFans page the current view copies ("Open on OnlyFans"). */
export function currentOfUrl(tab: TabId, subId: string, pills: Pick<Pills, 'referrals' | 'streakTab'>): string {
  const def = TABS.find((t) => t.id === tab)?.subs.find((s) => s.id === subId)
  let path = def?.path ?? 'statements/earnings'
  if (tab === 'statements' && subId === 'referrals') path = `statements/referrals/${pills.referrals}`
  if (tab === 'overview' && subId === 'streak') path = `overview/streaks?tab=${pills.streakTab === 'longest' ? 'top' : 'current'}`
  return `${OF_STATISTICS}/${path}`
}

// ── Formatting ───────────────────────────────────────────────────────────

export type Lang = 'en' | 'ru' | 'uk' | 'es'
export const langOf = (language: string | undefined): Lang => (language === 'ru' || language === 'uk' || language === 'es' ? language : 'en')
export const intlOf = (language: string | undefined) => ({ ru: 'ru-RU', uk: 'uk-UA', es: 'es-ES', en: 'en-US' })[langOf(language)]

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** "$1,234.50" — OnlyFans shows every amount in USD, en-US style. */
export function money(value: unknown, digits = 2): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(num(value))
}

export function int(value: unknown, group = true): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value ?? '0')
  return group ? new Intl.NumberFormat('en-US').format(n) : String(Math.round(n * 100) / 100)
}

/** The ↗ / ↘ change badge; null when there is no change to show. */
export function delta(value: unknown, signed = false): { up: boolean; text: string } | null {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return null
  const up = n > 0
  return { up, text: `${signed && !up ? '-' : ''}${Math.abs(n).toFixed(1).replace(/\.0$/, '')}%` }
}

/** "1d 08h:53m" / "0h:43m" (OF's view-duration format), from seconds. */
export function duration(seconds: unknown): string {
  const total = Math.max(0, Math.round(num(seconds)))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const hm = `${days ? String(hours).padStart(2, '0') : hours}h:${String(minutes).padStart(2, '0')}m`
  return days ? `${days}d ${hm}` : hm
}

/** Chart axis: "3h 20m" / "40m". */
export function shortDuration(seconds: unknown): string {
  const total = Math.max(0, Math.round(num(seconds)))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  return hours ? `${hours}h ${minutes}m` : `${minutes}m`
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit', year: 'numeric' }

export function formatDate(value: unknown, intl: string, opts: Intl.DateTimeFormatOptions = DATE_OPTS): string {
  const date = value instanceof Date ? value : new Date(String(value ?? ''))
  if (!Number.isFinite(date.getTime())) return '—'
  return date.toLocaleDateString(intl, opts)
}

/** "Oct 07, 2026, 04:40 am" in the viewer's local time, like OF lists. */
export function formatDateTime(value: unknown, intl: string): string {
  const date = new Date(String(value ?? ''))
  if (!Number.isFinite(date.getTime())) return '—'
  const day = date.toLocaleDateString(intl, DATE_OPTS)
  const time = date.toLocaleTimeString(intl, { hour: '2-digit', minute: '2-digit' }).toLowerCase()
  return `${day}, ${time}`
}

export function timezoneLabel(now = new Date()): string {
  const offset = -now.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const abs = Math.abs(offset)
  return `UTC ${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`
}

/** Visible text of OnlyFans' HTML (post text, descriptions). */
export function plain(html: unknown): string {
  const text = String(html ?? '')
  if (!text) return ''
  const doc = new DOMParser().parseFromString(`<body>${text}</body>`, 'text/html')
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

/** Country name in the UI language (OF sends it in English). */
export function countryName(code: unknown, fallback: string, intl: string): string {
  try {
    return new Intl.DisplayNames([intl], { type: 'region' }).of(String(code || '').toUpperCase()) || fallback
  } catch {
    return fallback
  }
}

export function flag(code: unknown): string {
  const cc = String(code || '').toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return ''
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
}

/** "$25" → "$25.00" (OF sends whole-dollar tips as strings). */
export const tipsText = (formatted: unknown, raw: unknown) => (formatted ? String(formatted).replace(/^\$(\d+)$/, '$$$1.00') : money(raw))

export function hourLabel(hour: number, intl: string): string {
  return new Date(Date.UTC(2026, 0, 1, hour)).toLocaleTimeString(intl, { hour: 'numeric', timeZone: 'UTC' }).toLowerCase().replace(/\s/g, '')
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// ── Period ───────────────────────────────────────────────────────────────

export interface Period {
  key: string
  from?: string
  to?: string
}

const pad = (n: number) => String(n).padStart(2, '0')

export function utc(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
}
export const utcDay = (date: Date) => utc(date).slice(0, 10)

export function parseUtc(text: unknown): Date | null {
  if (!text) return null
  const date = new Date(`${String(text).replace(' ', 'T')}Z`)
  return Number.isFinite(date.getTime()) ? date : null
}

export interface PeriodOption {
  key: string
  /** A key of the `period` group, or none when `text` is the label. */
  label?: string
  text?: string
  divider?: boolean
}

export function periodOptions(now: Date, intl: string): PeriodOption[] {
  const year = now.getUTCFullYear()
  const options: PeriodOption[] = [
    { key: 'custom', label: 'custom' },
    { key: '24h', label: '24h', divider: true },
    { key: '7d', label: '7d' },
    { key: '30d', label: '30d' },
    { key: '90d', label: '90d' },
    { key: '180d', label: '180d' },
    { key: '365d', label: '365d' },
    { key: `y${year}`, text: String(year), divider: true },
    { key: `y${year - 1}`, text: String(year - 1) },
    { key: 'all', label: 'all' },
  ]
  for (let i = 0; i < 6; i += 1) {
    const month = new Date(Date.UTC(year, now.getUTCMonth() - i, 1))
    options.push({
      key: `m${month.getUTCFullYear()}-${pad(month.getUTCMonth() + 1)}`,
      text: capitalize(month.toLocaleDateString(intl, { month: 'long', timeZone: 'UTC' })),
      divider: i === 0,
    })
  }
  return options
}

/** UTC query bounds, exactly as OF sends them (SPEC "Period dropdown"). */
export function computeRange(period: Period, startModel: string | null = null, now = new Date()): Range {
  const end = utc(now)
  const key = period.key
  const days = /^(\d+)d$/.exec(key)
  let startDate: string
  let endDate = end
  if (key === '24h') {
    startDate = utc(new Date(now.getTime() - DAY_MS))
  } else if (days) {
    startDate = `${utcDay(new Date(now.getTime() - Number(days[1]) * DAY_MS))} 00:00:00`
  } else if (/^y\d{4}$/.test(key)) {
    const y = Number(key.slice(1))
    startDate = `${y}-01-01 00:00:00`
    endDate = y === now.getUTCFullYear() ? end : `${y}-12-31 23:59:59`
  } else if (/^m\d{4}-\d{2}$/.test(key)) {
    const [y = 0, m = 0] = key.slice(1).split('-').map(Number)
    const last = new Date(Date.UTC(y, m, 0))
    startDate = `${key.slice(1)}-01 00:00:00`
    endDate = y === now.getUTCFullYear() && m === now.getUTCMonth() + 1 ? end : `${utcDay(last)} 23:59:59`
  } else if (key === 'all') {
    startDate = startModel || '2016-01-01 00:00:00'
  } else if (key === 'custom' && period.from && period.to) {
    startDate = `${period.from} 00:00:00`
    endDate = period.to >= utcDay(now) ? end : `${period.to} 23:59:59`
  } else {
    return computeRange({ key: '30d' }, null, now)
  }
  return { startDate, endDate }
}

/** The dropdown's second line: "Sep 09, 2026 - Oct 09, 2026" (UTC days), or times for 24 hours. */
export function periodSubtitle(key: string, range: Range | null, intl: string): string {
  const start = parseUtc(range?.startDate)
  const end = parseUtc(range?.endDate)
  if (!start || !end) return ''
  if (key === '24h') {
    const t = (d: Date) => d.toLocaleTimeString(intl, { hour: '2-digit', minute: '2-digit' }).toLowerCase()
    return `${t(start)}, ${formatDate(start, intl, { month: 'short', day: '2-digit' })} - ${t(end)}, ${formatDate(end, intl)}`
  }
  const opts: Intl.DateTimeFormatOptions = { ...DATE_OPTS, timeZone: 'UTC' }
  return `${formatDate(start, intl, opts)} - ${formatDate(end, intl, opts)}`
}

/** The custom-range dialog's start values: the current range's UTC days. */
export function customFromRange(range: Range | null, now = new Date()): { from: string; to: string } {
  const end = parseUtc(range?.endDate) ?? now
  const start = parseUtc(range?.startDate) ?? new Date(end.getTime() - 30 * DAY_MS)
  return { from: utcDay(start), to: utcDay(end) }
}

export function customDays(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Number.isFinite(a) && Number.isFinite(b) && b >= a ? Math.round((b - a) / DAY_MS) + 1 : 0
}

/** Cache key of a period's range: one range per choice for the page's lifetime. */
export const periodKey = (p: Period) => (p.key === 'custom' ? `custom:${p.from}:${p.to}` : p.key)

// ── Queries (the exact calls of each OnlyFans view) ──────────────────────

export const withRange = (range: Range, extra: Query = {}): Query => ({ startDate: range.startDate, endDate: range.endDate, ...extra })

export function earningsChartQuery(range: Range, filter: string): Query {
  return withRange(range, { withTotal: 'true', [`filter[${filter}_count]`]: `${filter}_count`, [`filter[${filter}_amount]`]: `${filter}_amount` })
}

export function payoutChartQuery(range: Range, path: string): Query {
  return withRange(range, {
    withTotal: path === 'payouts/referrals/chart' ? '1' : 'true',
    withChart: 'true',
    'filter[chartAmount]': 'chartAmount',
    'filter[chartCount]': 'chartCount',
  })
}

export function overviewChartQuery(range: Range): Query {
  const query = withRange(range, { withTotal: 'true' })
  OVERVIEW_ROWS.forEach((r) => {
    query[`filter[${r.filter}]`] = r.filter
  })
  return query
}

export const metricKind = (by: string): Kind => (by === 'purchases' || by === 'tips' ? 'money' : 'count')

export function campaignsQuery(range: Range): Query {
  return withRange(range, {
    limit: PAGE_SIZE,
    pagination: '1',
    field: 'subscribers_counts',
    with_deleted: '1',
    sorting_deleted: '0',
    stats: 'true',
    sort: 'desc',
  })
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

// ── Paged lists ──────────────────────────────────────────────────────────

export interface ListPage {
  rows: Raw[]
  cursor: unknown
  hasMore: boolean
}
export type Fetch = (path: string, query: Query) => Promise<unknown>
export type Loader = (cursor: unknown) => Promise<ListPage>

const rowsOf = (body: unknown, field: string): Raw[] => {
  const list = (body as Raw | null)?.[field]
  return Array.isArray(list) ? (list as Raw[]) : []
}

/** Offset-paged OF list ({list|items|users, hasMore}). */
export function offsetLoader(fetch: Fetch, path: string, query: Query, field = 'list'): Loader {
  return async (cursor) => {
    const offset = Number(cursor) || 0
    const body = (await fetch(path, { ...query, offset })) as Raw | null
    const rows = rowsOf(body, field)
    const hasMore = typeof body?.hasMore === 'boolean' ? body.hasMore : rows.length >= PAGE_SIZE
    return { rows, cursor: offset + rows.length, hasMore }
  }
}

/** payouts/transactions: paged by `marker` (seconds), first page from the range's end. */
export function transactionsLoader(fetch: Fetch, range: Range, tx: string): Loader {
  return async (cursor) => {
    const end = parseUtc(range.endDate)
    const query: Query = { startDate: range.startDate, marker: (cursor as number | null) ?? Math.floor((end?.getTime() ?? Date.now()) / 1000) }
    if (tx) query.type = tx
    const body = (await fetch('payouts/transactions', query)) as Raw | null
    const next = body?.nextMarker
    return { rows: rowsOf(body, 'list'), cursor: next ?? null, hasMore: Boolean(body?.hasMore) && next != null }
  }
}

/** Messages: OF pages this list by moving endDate back to the last message's date. */
export function messagesLoader(fetch: Fetch, range: Range, kind: 'group' | 'direct'): Loader {
  return async (cursor) => {
    const body = (await fetch(`users/me/stats/messages/${kind}`, { startDate: range.startDate, endDate: (cursor as string) || range.endDate, limit: PAGE_SIZE })) as Raw | null
    const rows = rowsOf(body, 'items')
    const last = rows[rows.length - 1]
    const lastDate = last ? new Date(String(last.date ?? last.createdAt ?? '')) : null
    const ok = lastDate && Number.isFinite(lastDate.getTime())
    return { rows, cursor: ok ? utc(lastDate) : null, hasMore: Boolean(body?.hasMore) && Boolean(lastDate) }
  }
}

/** A list's cache key: its call and the period it covers. */
export const listKeyOf = (name: string, startDate: string, endDate: string) => `${name}|${startDate}|${endDate}`

/** Appends a page, dropping rows already listed (by id). */
export function mergeRows(prev: Raw[], page: Raw[]): Raw[] {
  const seen = new Set(prev.map((r) => r?.id).filter((id) => id !== undefined && id !== null))
  return prev.concat(page.filter((r) => r?.id === undefined || r?.id === null || !seen.has(r.id)))
}

// ── Charts ───────────────────────────────────────────────────────────────

export type Kind = 'money' | 'count' | 'duration' | 'percent'
export interface Point {
  t: number
  v: number
}
export interface Series {
  points: unknown
  kind: Kind
  label: string
}

export function points(list: unknown): Point[] {
  return (Array.isArray(list) ? (list as Raw[]) : [])
    .map((p) => ({ t: Date.parse(String(p?.date ?? '')), v: Number(p?.count) || 0 }))
    .filter((p) => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t)
}

/** Axis top that splits into `steps` round (and, for counts, whole) ticks. */
export function niceMax(max: number, whole = false, steps = 3): number {
  if (!(max > 0)) return whole ? steps : steps * 5
  const raw = whole ? Math.max(1, max / steps) : max / steps
  const pow = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw && (!whole || Number.isInteger(s))) || 10 * pow
  return step * steps
}

export function axisLabel(value: number, kind: Kind): string {
  if (kind === 'money') return `$${int(value)}`
  if (kind === 'duration') return shortDuration(value)
  if (kind === 'percent') return `${Math.round(value)}%`
  return int(value)
}

export function valueLabel(value: number, kind: Kind): string {
  if (kind === 'money') return money(value)
  if (kind === 'duration') return shortDuration(value)
  return int(value)
}

export interface ChartModel {
  W: number
  H: number
  mainH: number
  times: number[]
  mainVals: number[]
  subVals: number[]
  monthly: boolean
  area: string
  line: string
  subArea: string
  subLine: string
  grid: number[]
  /** Labels on the right, `top` as a percentage of the plot height. */
  yLabels: { top: number; text: string }[]
  xTicks: { left: number; top: string; bottom: string; align: 'start' | 'middle' | 'end' }[]
  /** x of point i, in viewBox units. */
  xs: number[]
}

/** OF's statistics chart: a filled main series with a smaller second series underneath, both against the same days. */
export function chartModel(main: Series, sub: Series | null, intl: string): ChartModel | null {
  const mainPts = points(main.points)
  const subPts = sub ? points(sub.points) : []
  const times = [...new Set([...mainPts, ...subPts].map((p) => p.t))].sort((a, b) => a - b)
  if (!times.length) return null
  const W = 560
  const mainH = 100
  const subTop = mainH + 18
  const subH = sub ? 34 : 0
  const H = subTop + subH
  const valueAt = (pts: Point[]) => {
    const map = new Map(pts.map((p) => [p.t, p.v]))
    return times.map((t) => map.get(t) || 0)
  }
  const mainVals = valueAt(mainPts)
  const subVals = sub ? valueAt(subPts) : []
  // Durations are seconds: round them on whole minutes ("1h 40m", "3h 20m").
  const axisMax = (vals: number[], kind: Kind, steps: number) =>
    kind === 'duration' ? niceMax(Math.max(0, ...vals) / 60, true, steps) * 60 : niceMax(Math.max(0, ...vals), kind !== 'money', steps)
  const mainMax = axisMax(mainVals, main.kind, 3)
  const subMax = sub ? axisMax(subVals, sub.kind, 2) : 1
  const xs = times.map((_, i) => (times.length === 1 ? W / 2 : (i / (times.length - 1)) * W))
  const yMain = (v: number) => mainH - 4 - (v / mainMax) * (mainH - 10)
  const ySub = (v: number) => subTop + subH - 2 - (v / subMax) * (subH - 6)
  const xAt = (i: number) => xs[i] ?? 0
  const path = (vals: number[], y: (v: number) => number) => vals.map((v, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${y(v).toFixed(1)}`).join('')
  const lastX = xAt(xs.length - 1).toFixed(1)
  const firstX = xAt(0).toFixed(1)
  const line = path(mainVals, yMain)
  const subLine = sub ? path(subVals, ySub) : ''
  const grid = [1, 2, 3].map((k) => mainH - 4 - (k / 3) * (mainH - 10))
  const yLabels = [
    ...[1, 2, 3].map((k) => ({ top: ((mainH - 4 - (k / 3) * (mainH - 10)) / H) * 100, text: axisLabel((mainMax * k) / 3, main.kind) })),
    ...(sub ? [1, 2].map((k) => ({ top: (ySub((subMax * k) / 2) / H) * 100, text: axisLabel((subMax * k) / 2, sub.kind) })) : []),
  ]
  const tAt = (i: number) => times[i] ?? 0
  const span = tAt(times.length - 1) - tAt(0)
  const monthly = span > 200 * DAY_MS && times.length < 40
  const tickCount = Math.min(5, times.length)
  const xTicks = Array.from({ length: tickCount }, (_, k) => {
    const i = tickCount === 1 ? 0 : Math.round((k / (tickCount - 1)) * (times.length - 1))
    const date = new Date(tAt(i))
    const top = monthly
      ? date.toLocaleDateString(intl, { month: 'short', timeZone: 'UTC' })
      : `${date.toLocaleDateString(intl, { month: 'short', day: '2-digit', timeZone: 'UTC' })},`
    const align: 'start' | 'middle' | 'end' = k === 0 ? 'start' : k === tickCount - 1 ? 'end' : 'middle'
    return { left: (xAt(i) / W) * 100, top, bottom: String(date.getUTCFullYear()), align }
  })
  return {
    W,
    H,
    mainH,
    times,
    mainVals,
    subVals,
    monthly,
    line,
    area: `${line}L${lastX},${mainH}L${firstX},${mainH}Z`,
    subLine,
    subArea: sub ? `${subLine}L${lastX},${subTop + subH}L${firstX},${subTop + subH}Z` : '',
    grid,
    yLabels,
    xTicks,
    xs,
  }
}

/** The tooltip's date for point i. */
export function chartDate(model: ChartModel, i: number, intl: string): string {
  return new Date(model.times[i] ?? 0).toLocaleDateString(
    intl,
    model.monthly ? { month: 'long', year: 'numeric', timeZone: 'UTC' } : { ...DATE_OPTS, timeZone: 'UTC' },
  )
}

/** The small trend line of the summary cards; a flat line when there is nothing to draw. */
export function sparkPath(list: unknown): string | null {
  const pts = points(list)
  if (pts.length < 2) return null
  const max = Math.max(...pts.map((p) => p.v)) || 1
  return pts.map((p, i) => `${i ? 'L' : 'M'}${((i / (pts.length - 1)) * 130).toFixed(1)},${(38 - (p.v / max) * 34).toFixed(1)}`).join('')
}

// ── Media and users ──────────────────────────────────────────────────────

/** The first viewable media's thumbnail (the CRM's pickVaultThumbUrl order). */
export function thumbOf(media: unknown): { url: string; video: boolean } | null {
  const list = Array.isArray(media) ? (media as Raw[]) : []
  const first = list.find((m) => m && m.canView !== false) ?? list[0]
  if (!first) return null
  const files = (first.files && typeof first.files === 'object' && !Array.isArray(first.files) ? first.files : {}) as Record<string, { url?: unknown } | undefined>
  const nested = [files.squarePreview?.url, files.thumb?.url, files.preview?.url, files.cover?.url, files.poster?.url]
    .map((v) => String(v ?? '').trim())
    .find((v) => /^https?:\/\//i.test(v))
  const url = String(nested || first.thumb || first.thumbnail || first.preview || first.cover || first.poster || first.src || first.url || '').trim()
  return { url: /^https?:\/\//i.test(url) ? url : '', video: first.type === 'video' }
}

export function mediaCounts(media: unknown): { photos: number; videos: number; audios: number } {
  const list = Array.isArray(media) ? (media as Raw[]) : []
  return {
    photos: list.filter((m) => m?.type === 'photo' || m?.type === 'gif').length,
    videos: list.filter((m) => m?.type === 'video').length,
    audios: list.filter((m) => m?.type === 'audio').length,
  }
}

export function avatarUrl(user: Raw | null | undefined): string {
  const thumbs = (user?.avatarThumbs ?? {}) as Raw
  return String(thumbs.c144 || thumbs.c50 || user?.avatar || user?.avatar_url || '').trim()
}

/** Creator's custom name first (OF shows displayName), then "name @username". */
export function userNames(user: Raw | null | undefined): { id: string; primary: string; secondary: string } {
  const u = user ?? {}
  const primary = String(u.displayName || u.name || u.username || '—')
  const secondary = [u.displayName && u.name ? String(u.name) : '', u.username ? `@${u.username}` : ''].filter(Boolean).join(' ')
  return { id: u.id ? String(u.id) : '', primary, secondary }
}

/** A nested object of an OnlyFans answer ({} when missing): dig(d, 'visitors', 'earnings'). */
export const dig = (d: unknown, ...path: string[]): Raw => path.reduce<Raw>((o, k) => (o?.[k] ?? {}) as Raw, (d ?? {}) as Raw)

export function fanSums(user: Raw | null | undefined): Raw {
  const s = user?.subscribedOnData
  return s && typeof s === 'object' ? (s as Raw) : {}
}

/** OF descriptions are HTML ("Tip from <a href=...>Name</a>"): the text, with the fan's name as a chat link. */
export function descriptionParts(row: Raw | null | undefined): { text: string; fanId?: string }[] {
  const raw = String(row?.description ?? '').trim()
  if (!raw) {
    const type = (row?.descriptionDetails as Raw | undefined)?.type
    return [{ text: String(type || 'Operation') }]
  }
  const fanId = (row?.user as Raw | undefined)?.id ? String((row!.user as Raw).id) : ''
  const doc = new DOMParser().parseFromString(`<body>${raw}</body>`, 'text/html')
  const parts: { text: string; fanId?: string }[] = []
  doc.body.childNodes.forEach((node) => {
    const text = node.textContent || ''
    if (node.nodeType === Node.ELEMENT_NODE && fanId && text.trim()) parts.push({ text: text.trim(), fanId })
    else if (text) parts.push({ text })
  })
  return parts.length ? parts : [{ text: plain(raw) }]
}

export const invoiceUrl = (invoiceId: unknown, referral: boolean) =>
  `https://onlyfans.com/api2/v2/payouts/download-approved-invoice?invoiceId=${encodeURIComponent(String(invoiceId))}&app-token=${OF_APP_TOKEN}&isRefbonus=${referral ? 1 : 0}`

export const referralUrl = (id: unknown) => (id ? `https://onlyfans.com?ref=${encodeURIComponent(String(id))}` : '')

/** Promotions / trial links rows: OF names the same fields differently per list. */
export function offerRow(item: Raw) {
  const discount = item.discount ?? item.discountPercent
  return {
    claims: item.claimsCount ?? item.subscribeCounts ?? item.claims ?? item.countSubscribers ?? 0,
    created: item.createdAt || item.startDate || item.startedAt || null,
    finished: item.finishedAt || item.expiredAt || item.endDate || null,
    price: item.price ?? item.subscribePrice ?? null,
    title: plain(item.message || item.trialLinkName || item.name || '') || (discount ? `-${discount}%` : ''),
  }
}

/** A stream row's stats, only the ones OF sent. */
export function streamStats(stream: Raw): [label: string, value: string][] {
  const out: [string, unknown][] = [
    ['viewers', stream.viewersCount ?? stream.viewsCount ?? stream.views],
    ['likes', stream.likesCount ?? stream.likes],
    ['comments', stream.commentsCount ?? stream.comments],
    ['tips', stream.tipsAmount ?? (stream.tipsSum !== undefined ? money(stream.tipsSum) : undefined)],
    ['purchases', stream.purchasesCount ?? stream.purchasedCount],
    ['duration', stream.duration !== undefined ? shortDuration(stream.duration) : undefined],
  ]
  return out.filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, typeof v === 'number' ? int(v) : String(v)])
}

// ── Profile visitors ─────────────────────────────────────────────────────

/** "Others": the totals minus the listed countries (guests, users, total). */
export function countryOthers(topCountries: Raw | null | undefined): number[] {
  const rows = Array.isArray(topCountries?.rows) ? (topCountries!.rows as Raw[]) : []
  const totals = (topCountries?.totals ?? {}) as Raw
  const sum = (f: string) => rows.reduce((s, r) => s + (Number((r.viewsCount as Raw | undefined)?.[f]) || 0), 0)
  return ['guests', 'users', 'total'].map((f) => Math.max(0, (Number(totals[f]) || 0) - sum(f)))
}

// ── Activity streak ──────────────────────────────────────────────────────

export interface Month {
  y: number
  m: number
}

export function streakQuery({ y, m }: Month, now = new Date()): Query {
  const isCurrent = y === now.getFullYear() && m === now.getMonth()
  const last = new Date(y, m + 1, 0).getDate()
  return {
    startDate: `${y}-${pad(m + 1)}-01 00:00:00`,
    endDate: isCurrent
      ? `${y}-${pad(m + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      : `${y}-${pad(m + 1)}-${pad(last)} 23:59:59`,
  }
}

export const dayKey = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
export const localDayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** OF counts today once there is activity today; the streak row ends yesterday. */
export function streakDays(streak: Raw | null | undefined, todayActive: boolean, now = new Date()): number {
  if (!streak) return 0
  const days = Number(streak.daysCount) || 0
  if (!streak.isActive || !todayActive) return days
  const end = Date.parse(String(streak.endDate ?? ''))
  return Number.isFinite(end) && dayKey(new Date(end)) < localDayKey(now) ? days + 1 : days
}

export const isTodayActive = (today: Raw | null | undefined) => ['posts', 'stories', 'chats', 'streams'].some((k) => Number(today?.[k]) > 0)

export function shiftMonth({ y, m }: Month, step: number, now = new Date()): Month | null {
  const next = new Date(y, m + step, 1)
  if (next > now) return null
  return { y: next.getFullYear(), m: next.getMonth() }
}

export interface CalendarCell {
  day: number | null
  on: boolean
  today: boolean
}

/** The month grid: leading blanks, then each day lit when a streak covers it. */
export function streakCalendar({ y, m }: Month, list: unknown, todayActive: boolean, now = new Date()): CalendarCell[] {
  const isCurrent = y === now.getFullYear() && m === now.getMonth()
  const days = new Set<string>()
  let todayHit = false
  ;(Array.isArray(list) ? (list as Raw[]) : []).forEach((s) => {
    const start = Date.parse(String(s?.startDate ?? ''))
    const end = Date.parse(String(s?.endDate ?? ''))
    if (!Number.isFinite(start) || !Number.isFinite(end)) return
    for (let t = start; t <= end; t += DAY_MS) days.add(dayKey(new Date(t)))
    if (s.isActive && todayActive) todayHit = true
  })
  const cells: CalendarCell[] = []
  const first = new Date(y, m, 1).getDay()
  for (let i = 0; i < first; i += 1) cells.push({ day: null, on: false, today: false })
  const count = new Date(y, m + 1, 0).getDate()
  for (let d = 1; d <= count; d += 1) {
    const isToday = isCurrent && d === now.getDate()
    const on = days.has(`${y}-${pad(m + 1)}-${pad(d)}`) || (isToday && todayHit)
    cells.push({ day: d, on, today: isToday && on })
  }
  return cells
}

// ── Fans activity ────────────────────────────────────────────────────────

export const HEAT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const HEAT_SLOTS = [0, 3, 6, 9, 12, 15, 18, 21]

/** The weekly heatmap: each cell's ratio and its 0.25–1 shade, relative to the busiest slot. */
export function heatmap(data: unknown): { ratio: number; level: number }[][] {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Record<string, { ratio?: unknown } | undefined> | undefined>
  const ratio = (day: string, h: number) => Number(d[day]?.[h]?.ratio) || 0
  const all = HEAT_DAYS.flatMap((day) => HEAT_SLOTS.map((h) => ratio(day, h)))
  const min = Math.min(...all)
  const max = Math.max(...all)
  const level = (r: number) => (max > min ? (r - min) / (max - min) : 0.5)
  return HEAT_DAYS.map((day) => HEAT_SLOTS.map((h) => ({ ratio: ratio(day, h), level: 0.25 + level(ratio(day, h)) * 0.75 })))
}

/** The payout minimum (OnlyFans' default $20). */
export function minPayout(balances: Raw | null | undefined): number {
  const min = Number(balances?.minPayoutSumm)
  return Number.isFinite(min) && min > 0 ? min : 20
}
